/**
 * /cli — public landing page for @cartieai/cli + MCP server.
 *
 * v34.33 (Feb 2026): one-pager explaining the CLI + MCP integration,
 * with copy-paste install command and Claude Desktop config snippet.
 * Mirrors the engineer-first tone of cartieai.com.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Terminal as TerminalIcon, ArrowRight, Copy, Check, Bot, ChevronDown, ChevronUp, Zap,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { ENGINE_COUNT } from '../constants/product';

const INSTALL_CMD = 'npm install -g @cartieai/cli';
// v34.68 (Jun 3 2026) — Honest fallback added while npm publish is pending.
// The package is fully built at /app/cli/ but the user has not yet run
// `npm publish --access public` (needs their npm credentials). Until then,
// the GitHub-direct install actually works and we surface it as an honest
// "Preview build" path with a small badge explaining the npm status.
const INSTALL_CMD_GITHUB = 'npm install -g github:cartieai/cli';
const NPM_STATUS = 'live'; // 'preview' | 'live' — flipped to 'live' Jun 6 2026 after @cartieai/cli@0.1.0 published

const MCP_CONFIG = `{
  "mcpServers": {
    "cartie": {
      "command": "npx",
      "args": ["-y", "@cartieai/cli", "mcp"],
      "env": {
        "CARTIE_API": "https://cartieai.com",
        "CARTIE_API_KEY": "sk_live_..."
      }
    }
  }
}`;

const COMMANDS = [
  { cmd: 'cartie why "Saved this month" --amount 4210', desc: '2-sentence LLM explanation of any $ amount' },
  { cmd: 'cartie visits', desc: 'Live visitor count of your CARTIE landing' },
  { cmd: 'cartie engines', desc: `List all ${ENGINE_COUNT} proprietary engines + deep-link URLs` },
  { cmd: 'cartie configure --api https://cartie.acme.com', desc: 'Point at a self-hosted deployment' },
];

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-400 hover:text-emerald-300 transition-colors"
      data-testid="cli-copy-btn"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const CliPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
      <SEOHead
        title="@cartieai/cli — Read CARTIEAI cost data from the terminal | MCP for Claude Desktop · Cursor"
        description="Terminal-first FinOps. `cartie why` explains any $ amount via Claude Haiku. MCP server lets Claude Desktop, Cursor, Continue pull CARTIE cost data natively."
      />
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-emerald-500/[0.08] blur-[140px]" />
        <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-sky-500/[0.05] blur-[120px]" />
      </div>

      <header className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="cli-nav-home">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-7 h-7 object-contain" />
            <span className="font-display font-black text-base tracking-tight">
              <span className="text-white">CARTIE</span><span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-emerald-300 inline-flex items-center gap-1">
            ← Back home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16 sm:py-24">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
            <TerminalIcon className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-200">@cartieai/cli · v0.1.0</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-[-0.03em] leading-[1.05] mb-5">
            Your cost data{' '}
            {/* v34.58 — was bg-clip-text gradient ending at sky-400 which faded
                the last letters of "terminal" to look like glare. Switched to a
                solid bright emerald + drop-shadow glow that matches the brand
                and stays fully readable. */}
            <span className="text-emerald-300 drop-shadow-[0_0_24px_rgba(16,185,129,0.45)]">
              in the terminal.
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Most FinOps tools require you to log into a dashboard. CARTIE is built engineer-first — talk to your costs from where you already work.
          </p>
        </div>

        {/* Install */}
        <div className="rounded-2xl border border-emerald-500/30 bg-slate-900 overflow-hidden mb-10" data-testid="cli-install-card">
          <div className="px-4 py-2 border-b border-emerald-500/20 bg-slate-950/40 flex items-center justify-between flex-wrap gap-2">
            <span className="text-[11px] font-mono text-slate-400">install · macos · linux · windows</span>
            <div className="flex items-center gap-3">
              {NPM_STATUS === 'preview' && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300"
                  data-testid="cli-npm-preview-badge"
                  title="The npm package is in pre-launch QA. Use the GitHub install below until it goes live."
                >
                  npm · preview
                </span>
              )}
              <CopyButton text={INSTALL_CMD} />
            </div>
          </div>
          <pre className="px-5 py-5 font-mono text-emerald-300 text-base overflow-x-auto">
            <span className="text-slate-500">$ </span>{INSTALL_CMD}
          </pre>

          {/* v34.68 (Jun 3 2026) — Honest backup install path while npm is in QA */}
          {NPM_STATUS === 'preview' && (
            <div className="px-5 py-4 border-t border-emerald-500/15 bg-slate-950/40">
              <div className="flex items-start gap-3">
                <div className="w-1 h-12 rounded-full bg-emerald-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-slate-300 mb-2 leading-relaxed">
                    The npm package goes live with the public launch. Until then, install directly from GitHub — same code, same MCP server, works today:
                  </p>
                  <div className="flex items-center justify-between gap-3 bg-slate-900 rounded-lg px-3 py-2">
                    <code className="font-mono text-[12px] text-emerald-300 break-all" data-testid="cli-install-github">
                      <span className="text-slate-500">$ </span>{INSTALL_CMD_GITHUB}
                    </code>
                    <CopyButton text={INSTALL_CMD_GITHUB} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Commands */}
        <h2 className="text-2xl font-bold text-white mb-4 mt-14">Commands</h2>
        <div className="space-y-3 mb-14">
          {COMMANDS.map((c, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-slate-900/60 overflow-hidden" data-testid={`cli-cmd-${i}`}>
              <div className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                <code className="font-mono text-[13px] text-emerald-300 break-all">{c.cmd}</code>
                <CopyButton text={c.cmd} />
              </div>
              <div className="px-4 py-2 border-t border-white/5 bg-slate-950/30">
                <span className="text-xs text-slate-400">{c.desc}</span>
              </div>
            </div>
          ))}
        </div>

        {/* MCP section */}
        <div className="rounded-2xl border border-sky-500/30 bg-slate-900/60 overflow-hidden mb-10" data-testid="cli-mcp-card">
          <div className="px-5 py-4 border-b border-sky-500/20 bg-slate-950/40">
            <div className="flex items-center gap-2 mb-1">
              <Bot className="w-5 h-5 text-sky-300" />
              <h3 className="text-xl font-bold text-white">Talk to your costs inside Claude Desktop · Cursor · Continue · Zed</h3>
            </div>
            <p className="text-sm text-slate-400">CARTIE ships a Model Context Protocol server. Drop this into your MCP-aware client and ask cost questions in plain English.</p>
          </div>
          <div className="px-4 py-2 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
            <span className="text-[11px] font-mono text-slate-500">claude_desktop_config.json</span>
            <CopyButton text={MCP_CONFIG} label="Copy config" />
          </div>
          <pre className="px-5 py-5 font-mono text-[12.5px] text-slate-200 overflow-x-auto bg-slate-950/40">
{MCP_CONFIG}
          </pre>
          <div className="px-5 py-4 border-t border-sky-500/20 bg-slate-950/40">
            <div className="text-[11px] uppercase tracking-widest text-sky-300 mb-2">Available tools</div>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div><code className="text-emerald-300 font-mono">cartie_why</code> <span className="text-slate-400">— explain how a $ amount was computed</span></div>
              <div><code className="text-emerald-300 font-mono">cartie_visits</code> <span className="text-slate-400">— live landing visitor count</span></div>
              <div><code className="text-emerald-300 font-mono">cartie_engines</code> <span className="text-slate-400">— list the {ENGINE_COUNT} engines</span></div>
              <div><code className="text-emerald-300 font-mono">cartie_wall_feed</code> <span className="text-slate-400">— live activity ticker</span></div>
            </div>
          </div>
        </div>

        {/* Why */}
        <div className="rounded-xl border border-white/10 bg-slate-900/40 px-6 py-6 mb-14">
          <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-3">Why this exists</div>
          <ul className="space-y-2 text-sm text-slate-300 leading-relaxed">
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">1.</span> <span><strong className="text-white">Terminal-native.</strong> <code className="text-emerald-300 font-mono">cartie why</code> is faster than opening a tab.</span></li>
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">2.</span> <span><strong className="text-white">AI-native.</strong> Your IDE assistant can see your cost data without you copy-pasting screenshots.</span></li>
            <li className="flex gap-3"><span className="text-emerald-400 shrink-0">3.</span> <span><strong className="text-white">MCP-native.</strong> Claude Desktop, Cursor, Continue, Zed, and any future MCP-aware client gets CARTIE for free.</span></li>
          </ul>
        </div>

        {/* CTA row */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/developers/quickstart" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors" data-testid="cli-cta-quickstart">
            <Zap className="w-4 h-4" />
            5-line API quickstart
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="https://github.com/cartieai/cli" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-white/30 text-slate-200 font-semibold text-sm transition-colors">
            View on GitHub <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
    </div>
  );
};

export default CliPage;
